<template>
  <div
    class="ra-page"
    :class="{ 'ra-page--embedded': embedded }"
  >
    <div class="ra-header">
      <h4 v-if="!embedded">
        Доступность справочников
      </h4>
      <p
        v-if="!embedded"
        class="ra-hint"
      >
        Правила фильтрации записей справочников. Когда пользователь выбирает значение в ref-поле,
        список ограничивается по этим правилам — по entity (справочник), объекту (форма/куб) и роли.
      </p>
      <div
        v-if="embedded && (filterEntityId || filterObjectId || filterRefObjectId)"
        class="ra-context"
      >
        Показаны правила:
        <span v-if="filterEntityId"> для entity <strong>{{ entityLabel(filterEntityId) }}</strong></span>
        <span v-if="filterRefObjectId"> для этого справочника (через все ref-entities, ссылающиеся на него)</span>
        <span v-if="filterObjectId"> в объекте-назначении <strong>{{ objectLabel(filterObjectId) }}</strong></span>
      </div>
      <div class="ra-actions">
        <b-button
          size="sm"
          variant="primary"
          @click="openCreate"
        >
          <feather-icon
            icon="PlusIcon"
            size="14"
            class="mr-25"
          /> Добавить правило
        </b-button>
        <b-button
          size="sm"
          variant="outline-secondary"
          @click="loadAll"
        >
          <feather-icon
            icon="RefreshCwIcon"
            size="14"
            class="mr-25"
          /> Обновить
        </b-button>
      </div>
    </div>

    <div
      v-if="loading"
      class="ra-loading"
    >
      Загрузка…
    </div>

    <div
      v-else-if="filteredRules.length === 0"
      class="ra-empty"
    >
      <span v-if="embedded">Для этого объекта/справочника правил пока нет.</span>
      <span v-else>Правил пока нет. Нажми «Добавить правило» чтобы создать.</span>
    </div>

    <div
      v-else
      class="ra-table-wrap"
    >
      <table class="ra-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Справочник (entity)</th>
            <th>Объект применения</th>
            <th>Роль</th>
            <th>Фильтр</th>
            <th />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="r in filteredRules"
            :key="r.id"
            :class="{ 'ra-row--del': r.del }"
          >
            <td class="ra-num">
              {{ r.id }}
            </td>
            <td>{{ entityLabel(r.entityid) }}</td>
            <td>{{ r.objectid ? objectLabel(r.objectid) : '— любой —' }}</td>
            <td>{{ r.roleid ? roleLabel(r.roleid) : '— любая —' }}</td>
            <td class="ra-filter-cell">
              <span
                class="ra-filter-preview"
                :title="r.jsonfilter"
              >{{ filterPreview(r.jsonfilter) }}</span>
            </td>
            <td class="ra-actions-cell">
              <button
                class="ra-btn ra-btn--edit"
                title="Редактировать правило"
                @click="openEdit(r)"
              >
                <feather-icon
                  icon="Edit2Icon"
                  size="13"
                />
                <span>Изменить</span>
              </button>
              <button
                class="ra-btn ra-btn--del"
                title="Удалить правило"
                @click="remove(r)"
              >
                <feather-icon
                  icon="TrashIcon"
                  size="13"
                />
                <span>Удалить</span>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Wizard -->
    <cbs-ref-availability-wizard
      :show="wizardOpen"
      :catalog="aiCatalog"
      :catalog-records="aiCatalogRecords"
      :catalog-target-entities="catalogTargetEntities"
      :catalog-ref-attributes="catalogRefAttributes"
      :context-catalogs="aiContextCatalogs"
      :ref-entities="refEntities"
      :roles="allRoles"
      :objects="allObjects"
      :initial-rule="editing"
      @save="onWizardSave"
      @saved="loadRules"
      @close="wizardOpen = false"
    />
  </div>
</template>

<script>
import { BButton } from 'bootstrap-vue'
import { getSessionToken } from '@/cubus/jwt/tokenHolder'
import useCubus from '@/cubus/services/useCubus'
import CbsRefAvailabilityWizard from './CbsRefAvailabilityWizard.vue'

export default {
  name: 'CbsRefAvailability',
  components: { BButton, CbsRefAvailabilityWizard },
  props: {
    embedded: { type: Boolean, default: false },
    filterEntityId: { type: Number, default: null },
    filterObjectId: { type: Number, default: null },
    filterRefObjectId: { type: Number, default: null }, // filter rules where entityid → entity has refobjectid = this
  },
  data() {
    return {
      loading: false,
      rules: [],
      refEntities: [], // entities with refobjectid > 0
      allObjects: [],
      allRoles: [],
      wizardOpen: false,
      editing: null, // { id, sid, del, entityid, objectid, roleid, jsonfilter } — passed to wizard
      aiCatalogRecords: [], // loaded when filterRefObjectId is set
      aiCatalog: null, // { id, name, sid, attributes }
      aiFormParams: [], // typical paramentity sources (scenario, year, BU)
      aiContextCatalogs: [], // common context catalogs (subdivisions, scenarios, years, ...) with their records
    }
  },
  computed: {
    // entityIds whose refobjectid equals filterRefObjectId — used to filter rules by catalog
    relatedEntityIds() {
      if (!this.filterRefObjectId) return null
      return new Set(
        this.refEntities
          .filter(e => Number(e.refobjectid) === Number(this.filterRefObjectId))
          .map(e => e.id),
      )
    },
    filteredRules() {
      return this.rules.filter(r => {
        if (this.filterEntityId && r.entityid !== this.filterEntityId) return false
        if (this.filterObjectId && r.objectid !== this.filterObjectId) return false
        if (this.relatedEntityIds && !this.relatedEntityIds.has(r.entityid)) return false
        return true
      })
    },
    aiRelatedRefEntities() {
      return this.refEntities
    },
    catalogTargetEntities() {
      // ref-entities pointing to current catalog
      if (!this.aiCatalog || !this.aiCatalog.id) return []
      const targetId = Number(this.aiCatalog.id)
      const result = this.refEntities.filter(e => Number(e.refobjectid) === targetId)
      // eslint-disable-next-line no-console
      console.log('[RefAvail] catalogTargetEntities for catalog', targetId,
        '— found', result.length, 'of', this.refEntities.length, 'total ref entities')
      return result
    },
    catalogRefAttributes() {
      // ref-attributes of current catalog (fields linking to other catalogs)
      if (!this.aiCatalog) return []
      return (this.aiCatalog.attributes || [])
        .filter(a => a.refobjectid && Number(a.refobjectid) > 0)
        .map(a => ({
          entityid: a.entityid,
          sid: a.sid,
          name: a.name,
          datatype: a.datatype,
          refobjectid: a.refobjectid,
        }))
    },
  },
  mounted() {
    this.loadAll()
  },
  methods: {
    async loadAll() {
      this.loading = true
      try {
        await Promise.all([
          this.loadRules(),
          this.loadRefEntities(),
          this.loadObjects(),
          this.loadRoles(),
        ])
        // Load AI context if scoped to a catalog
        if (this.filterRefObjectId) {
          await this.loadAiCatalog()
          await this.loadAiCatalogRecords()
        }
        this.loadAiFormParams()
        // Context catalogs (subdivisions etc.) — for "per X" rule generation
        this.loadAiContextCatalogs()
      } catch (e) {
        console.error('loadAll error', e)
      }
      this.loading = false
    },

    async loadAiCatalog() {
      try {
        const objFull = await useCubus.loadObject(this, { objectid: this.filterRefObjectId })
        if (objFull?.object) {
          this.aiCatalog = {
            id: objFull.object.id,
            sid: objFull.object.sid,
            name: objFull.object.name,
            attributes: (objFull.attributes || []).map(a => ({
              attributeid: a.attributeid || a.id,
              entityid: a.entityid,
              sid: a.entitysid,
              name: a.label || a.entitysid,
              datatype: a.datatype,
              refobjectid: a.refobjectid || 0,
            })),
          }
          // eslint-disable-next-line no-console
          console.log('[RefAvail] catalog loaded:', this.aiCatalog.name, 'attrs sample:', this.aiCatalog.attributes.slice(0, 3))
        }
      } catch (e) { console.error('loadAiCatalog', e) }
    },

    async loadAiCatalogRecords() {
      try {
        const qry = await useCubus.loadQuery(this, {
          objectid: this.filterRefObjectId,
          keymode: 'sid',
          pageSize: 5000,
        })
        const recs = qry?.recordset?.records || []
        if (recs[0]) console.log('[RefAvail] catalog record sample keys:', Object.keys(recs[0]))
        this.aiCatalogRecords = recs
          .filter(r => !r.del?.value)
          .map(r => ({
            id: r.id?.value,
            sid: r.sid?.value,
            name: r.name?.value,
            code: r.code?.value,
            isfolder: !!(r.isfolder?.value ?? r.isFolder?.value),
            parentid: Number(r.parent?.value ?? r.parentid?.value ?? r.parentId?.value ?? 0) || null,
          }))
      } catch (e) { console.error('loadAiCatalogRecords', e) }
    },

    loadAiFormParams() {
      // Common form params (BU, scenario, year). Take from required attributes of common form objects.
      // Heuristic: search refEntities for entities with sids like 'business_unit', 'scenario', 'year'
      const wanted = ['business_unit', 'scenario', 'year', 'company']
      this.aiFormParams = this.refEntities
        .filter(e => wanted.includes((e.sid || '').toLowerCase()))
        .map(e => ({ entityId: e.id, name: e.name }))
    },

    async loadAiContextCatalogs() {
      // Load records of "context" catalogs — those typically used as paramentity (subdivisions, scenarios, years, business units, schools, etc.)
      // ONLY actual catalogs (metaid=2). Excludes mounts/imports/queries that incidentally have "Подразд" in the name.
      const knownContextSids = new Set([
        'business_unit', 'subdivision', 'unit', 'unitmr', 'refunit', 'refunitmr',
        'scenario', 'refscenario', 'year', 'refyear', 'school', 'company', 'refcompany',
      ])
      const candidates = this.allObjects.filter(o => {
        if (o.metaid !== 2) return false // catalogs only
        const sid = (o.sid || '').toLowerCase()
        const name = (o.name || '').toLowerCase()
        if (knownContextSids.has(sid)) return true
        // Catalog name heuristic — only real ref* catalogs
        if (sid.startsWith('ref') && /подразд|сценари|год|школ|филиал|компани|единиц/.test(name)) return true
        return false
      }).slice(0, 8)
      // eslint-disable-next-line no-console
      console.log('[RefAvail] context catalog candidates:', candidates.map(c => `${c.name} (${c.sid}, meta=${c.metaid})`))

      const out = []
      for (const o of candidates) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const qry = await useCubus.loadQuery(this, { objectid: o.id, keymode: 'sid', pageSize: 500 })
          const rawRecs = qry?.recordset?.records || []
          if (rawRecs.length > 0) {
            // eslint-disable-next-line no-console
            console.log('[RefAvail] context catalog', o.name, '— first raw record keys:', Object.keys(rawRecs[0]), 'id field:', rawRecs[0].id, 'recordid:', rawRecs[0].recordid)
          }
          const extractId = r => {
            // Try multiple paths — Cubus records can store id under different keys depending on keymode
            const candidates2 = [
              r.id?.value, r.id,
              r.recordid?.value, r.recordid,
              r.recid?.value, r.recid,
            ]
            for (const c of candidates2) {
              const n = Number(c)
              if (Number.isFinite(n) && n > 0) return n
            }
            return null
          }
          const recs = rawRecs
            .filter(r => !r.del?.value)
            .map(r => ({
              id: extractId(r),
              sid: r.sid?.value || r.sid || '',
              name: r.name?.value || r.name || '',
              code: r.code?.value || r.code || '',
            }))
            .filter(r => r.id)
          out.push({
            id: o.id, sid: o.sid, name: o.name, records: recs,
          })
          // eslint-disable-next-line no-console
          console.log('[RefAvail] context catalog', o.name, '— loaded', recs.length, 'records, sample:', recs.slice(0, 3))
        } catch (e) { console.error('[RefAvail] loadAiContextCatalogs', o.name, e) }
      }
      this.aiContextCatalogs = out
    },

    async loadRules() {
      const qry = await useCubus.loadQuery(this, {
        keymode: 'sid',
        objectsid: 'reference_availability',
        pageSize: 5000,
      })
      const recs = qry?.recordset?.records || []
      this.rules = recs.map(r => {
        // jsonfilter could be: object, JSON-string, or double-encoded (legacy bad save). Normalize to string.
        let jf = r.jsonfilter?.value
        if (jf == null) jf = ''
        if (typeof jf === 'object') jf = JSON.stringify(jf)
        // If it's a string starting with '"' (encoded twice) — unwrap once
        if (typeof jf === 'string' && jf.length > 1 && jf[0] === '"') {
          try {
            const inner = JSON.parse(jf)
            if (typeof inner === 'string') jf = inner
          } catch (e) { /* keep as-is */ }
        }
        return {
          id: r.id?.value,
          sid: r.sid?.value,
          del: !!r.del?.value,
          entityid: r.entityid?.value || r.entity?.value || null,
          objectid: r.objectid?.value || r.object?.value || null,
          roleid: r.roleid?.value || r.role?.value || null,
          jsonfilter: jf,
          _raw: r,
        }
      })
    },

    async loadRefEntities() {
      // Entities with refobjectid > 0. Use limit/offset (no keymode) via useJwt directly —
      // useCubus.loadQuery with objectsid=entity returns 0 records (system table quirk).
      const useJwt = require('@/cubus/jwt/useJwt').default
      const token = getSessionToken()
      const resp = await this.runApiQuery(useJwt, token, 'query', {
        objectsid: 'entity', limit: 10000, offset: 0,
      })
      const records = resp?.query?.recordset?.records || resp?.recordset?.records || []
      this.refEntities = records
        .map(r => ({
          id: Number(r.id?.value),
          sid: r.sid?.value || '',
          name: r.name?.value || r.sid?.value || '',
          refobjectid: Number(r.refobjectid?.value) || 0,
          del: !!r.del?.value,
        }))
        .filter(e => e.refobjectid > 0 && !e.del)
        .sort((a, b) => String(a.name).localeCompare(String(b.name), 'ru'))
      // eslint-disable-next-line no-console
      console.log('[RefAvail] loaded ref entities:', this.refEntities.length, 'samples:', this.refEntities.slice(0, 3))
    },

    async runApiQuery(useJwt, token, method, param) {
      const resp = await useJwt.query({ token, query: { method, mode: 'sync', param } })
      const d = resp.data
      if (d.thread) {
        for (let i = 0; i < 60; i += 1) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise(r => setTimeout(r, 500))
          // eslint-disable-next-line no-await-in-loop
          const tr = await useJwt.query({ token, query: { method: 'thread', param: { threadname: d.thread } } })
          if (tr.data.thread?.status === 'done') return tr.data.thread.result
          if (tr.data.thread?.status === 'error') throw new Error(tr.data.thread.error || 'thread error')
        }
        throw new Error('thread timeout')
      }
      if (d.error) throw new Error(d.error)
      return d.result || d
    },

    async loadObjects() {
      const qry = await useCubus.loadQuery(this, {
        keymode: 'sid',
        objectsid: 'object',
        pageSize: 5000,
      })
      const recs = qry?.recordset?.records || []
      this.allObjects = recs
        .filter(r => !r.del?.value && r.name?.value)
        .map(r => ({
          id: r.id?.value,
          sid: r.sid?.value,
          name: r.name?.value,
          metaid: Number(r.metaid?.value ?? r.meta?.value ?? 0),
        }))
        .sort((a, b) => String(a.name).localeCompare(String(b.name), 'ru'))
    },

    async loadRoles() {
      const qry = await useCubus.loadQuery(this, {
        keymode: 'sid',
        objectsid: 'role',
        pageSize: 500,
      })
      const recs = qry?.recordset?.records || []
      this.allRoles = recs
        .filter(r => !r.del?.value)
        .map(r => ({
          id: r.id?.value,
          sid: r.sid?.value,
          name: r.name?.value || r.sid?.value,
        }))
    },

    entityLabel(id) {
      if (!id) return '—'
      const e = this.refEntities.find(x => x.id === id)
      return e ? `${e.name} (${e.sid})` : `#${id}`
    },
    objectLabel(id) {
      if (!id) return '—'
      const o = this.allObjects.find(x => x.id === id)
      return o ? `${o.name} (#${id})` : `#${id}`
    },
    roleLabel(id) {
      if (!id) return '—'
      const r = this.allRoles.find(x => x.id === id)
      return r ? r.name : `#${id}`
    },

    filterPreview(json) {
      if (!json) return '—'
      try {
        const obj = typeof json === 'string' ? JSON.parse(json) : json
        const root = obj?.node || obj
        const conds = root?.conds || []
        const nodes = root?.nodes || []
        const parts = []

        const leftLabel = c => {
          const a = c.args?.[0]
          if (!a) return '?'
          if (a.type?.sid === 'entity') return a.value?.entitysid || a.entitysid || a.value?.title || '?'
          if (a.type?.sid === 'paramentity') return `paramentity(${a.value?.title || a.value?.value || a.entityid})`
          if (a.type?.sid === 'entitysid') return a.value
          return '?'
        }
        const rightLabel = c => {
          const r = c.args?.[1]
          if (!r) return '?'
          if (r.type?.sid === 'value') return typeof r.value === 'object' ? r.value?.title || r.value?.value : r.value
          if (r.type?.sid === 'paramentity') return `paramentity(${r.value?.title || r.entityid})`
          if (r.type?.sid === 'formula') return `formula(${r.value?.title || r.value?.value})`
          return String(r.value ?? '?').slice(0, 30)
        }
        const formatOr = orNode => {
          const cc = orNode.conds || []
          if (cc.length === 0) return '(пусто)'
          if (cc.length > 4) return `${leftLabel(cc[0])} ∈ [${cc.length} значений]`
          return `${leftLabel(cc[0])} ∈ [${cc.map(rightLabel).join(', ')}]`
        }

        nodes.forEach(n => {
          if (n.oper?.sid === 'or') parts.push(formatOr(n))
          else parts.push('(подгруппа)')
        })
        conds.forEach(c => {
          const op = c.oper?.sid === 'equal' ? '=' : c.oper?.sid
          parts.push(`${leftLabel(c)} ${op} ${rightLabel(c)}`)
        })
        return parts.length === 0 ? '(пусто)' : parts.join(' AND ')
      } catch (e) {
        return String(json).slice(0, 60) + (String(json).length > 60 ? '…' : '')
      }
    },

    openCreate() {
      this.editing = null
      this.wizardOpen = true
    },

    openEdit(r) {
      this.editing = { ...r }
      this.wizardOpen = true
    },

    async onWizardSave({ type, id, rule }) {
      try {
        if (type === 'create') {
          const tpl = await this.apiCall('newrecord', { objectsid: 'reference_availability', keymode: 'sid' })
          const rec = tpl?.record?.record || tpl?.record
          if (!rec) throw new Error('Не получен шаблон записи')
          this.fillRuleRecord(rec, rule)
          await this.apiCall('saverecord', { objectsid: 'reference_availability', keymode: 'sid', record: rec })
        } else if (type === 'update') {
          const r = this.rules.find(x => x.id === id)
          if (!r) throw new Error(`Правило #${id} не найдено`)
          const rec = r._raw
          this.fillRuleRecord(rec, rule)
          await this.apiCall('saverecord', { objectsid: 'reference_availability', keymode: 'sid', record: rec })
        }
      } catch (e) {
        this.$bvToast.toast(`Ошибка: ${e.message || e}`, { title: 'Ошибка', variant: 'danger', solid: true })
      }
    },

    fillRuleRecord(rec, rule) {
      const set = (sid, val) => {
        if (rec[sid]) { rec[sid].value = val; rec[sid].status = 'changed' } else rec[sid] = { value: val, status: 'changed' }
      }
      // Parse string → object so backend serializes once (DB column expects pure JSON, not JSON-encoded string)
      let jsonObj = rule.jsonfilter
      if (typeof jsonObj === 'string') {
        try { jsonObj = JSON.parse(jsonObj) } catch (e) {
          jsonObj = {
            node: {
              isactive: false, oper: { sid: 'and' }, nodes: [], conds: [],
            },
          }
        }
      }
      set('entity', rule.entityid)
      set('entityid', rule.entityid)
      set('object', rule.objectid)
      set('objectid', rule.objectid)
      set('role', rule.roleid)
      set('roleid', rule.roleid)
      set('jsonfilter', jsonObj)
      set('del', false)
      // eslint-disable-next-line no-console
      console.log('[RefAvail] fillRuleRecord — jsonfilter object:', jsonObj, 'rec keys:', Object.keys(rec))
    },

    async remove(r) {
      const ok = await this.$bvModal.msgBoxConfirm(
        `Удалить правило #${r.id} (${this.entityLabel(r.entityid)})?`,
        {
          title: 'Подтверждение', okTitle: 'Удалить', cancelTitle: 'Отмена', okVariant: 'danger',
        },
      )
      if (!ok) return
      const rec = r._raw
      if (rec.del) { rec.del.value = true; rec.del.status = 'changed' } else rec.del = { value: true, status: 'changed' }
      try {
        await this.apiCall('saverecord', { objectsid: 'reference_availability', keymode: 'sid', record: rec })
        this.$bvToast.toast('Удалено', {
          title: 'Успех', variant: 'success', solid: true, autoHideDelay: 2000,
        })
        this.loadRules()
      } catch (e) {
        this.$bvToast.toast(`Ошибка: ${e.message || e}`, { title: 'Ошибка', variant: 'danger', solid: true })
      }
    },

    async apiCall(method, param) {
      const useJwt = require('@/cubus/jwt/useJwt').default
      const token = getSessionToken()
      const resp = await useJwt.query({ token, query: { method, mode: 'sync', param } })
      const d = resp.data
      if (d.thread) {
        for (let i = 0; i < 60; i += 1) {
          await new Promise(r => setTimeout(r, 500))
          const tr = await useJwt.query({ token, query: { method: 'thread', param: { threadname: d.thread } } })
          if (tr.data.thread?.status === 'done') return tr.data.thread.result
          if (tr.data.thread?.status === 'error') throw new Error(tr.data.thread.error || 'thread error')
        }
        throw new Error('thread timeout')
      }
      if (d.error) throw new Error(d.error)
      return d.result || d
    },
  },
}
</script>

<style scoped>
.ra-page { padding: 24px; max-width: 1300px; margin: 0 auto; }
.ra-page--embedded { padding: 0; max-width: unset; }
.ra-context { font-size: 12px; color: #666; margin-bottom: 10px; padding: 6px 10px; background: #f3e5f5; border-radius: 4px; }
.ra-header { margin-bottom: 16px; }
.ra-header h4 { margin: 0 0 6px; font-size: 18px; color: #333; }
.ra-hint { font-size: 12px; color: #777; margin-bottom: 12px; line-height: 1.5; }
.ra-actions { display: flex; gap: 8px; }
.ra-loading, .ra-empty { padding: 40px; text-align: center; color: #999; background: #fafafa; border-radius: 6px; }
.ra-table-wrap { border: 1px solid #eee; border-radius: 6px; overflow: hidden; background: #fff; }
.ra-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.ra-table th { padding: 10px 12px; background: #f5f5f5; font-weight: 600; font-size: 11px; text-transform: uppercase; color: #555; text-align: left; border-bottom: 1px solid #ddd; }
.ra-table td { padding: 10px 12px; border-bottom: 1px solid #f0f0f0; vertical-align: top; }
.ra-table tr:hover { background: #fafbff; }
.ra-row--del { opacity: 0.5; }
.ra-num { color: #999; font-family: monospace; width: 50px; }
.ra-filter-cell { font-family: monospace; font-size: 11px; color: #555; max-width: 400px; }
.ra-filter-preview { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ra-actions-cell { width: 200px; white-space: nowrap; }
.ra-btn {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 4px 10px; margin-right: 4px;
  border: 1px solid #e4e4e7; background: #fff;
  border-radius: 6px; font-size: 12px; color: #52525b;
  cursor: pointer; transition: all 0.12s ease;
}
.ra-btn:hover { background: #fafafa; }
.ra-btn--edit:hover { color: #7c3aed; border-color: #c4b5fd; background: #faf5ff; }
.ra-btn--del:hover { color: #dc2626; border-color: #fca5a5; background: #fef2f2; }

.ra-form-row { margin-bottom: 14px; }
.ra-lbl { display: block; font-size: 12px; color: #555; margin-bottom: 4px; font-weight: 600; }
.ra-req { color: #e74c3c; margin-left: 2px; }
.ra-input { width: 100%; padding: 6px 10px; font-size: 13px; border: 1px solid #ddd; border-radius: 4px; background: #fff; }
.ra-textarea { width: 100%; padding: 8px 10px; font-size: 11px; font-family: monospace; border: 1px solid #ddd; border-radius: 4px; background: #fafafa; resize: vertical; }
.ra-help { font-size: 11px; color: #888; margin-top: 4px; line-height: 1.4; }
.ra-link { background: none; border: none; color: #7367f0; cursor: pointer; padding: 0; font-size: 11px; margin-left: 6px; }
.ra-link:hover { text-decoration: underline; }
</style>
